import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import moment from 'moment';
import classes from './tabs.module.scss';
import Checkbox from '../../../../_common/Form/Checkbox/Checkbox';
import InputField from '../../../../_common/Form/TextFields/InputField';
import DOBPicker from '../../../../_common/Form/DatePickers/DOBPicker/DOBPicker';
import Button from '../../../../_common/Form/Buttons/Button';
import utils from '../../../../../utils/utils';
import Modal from '../../../../_common/Modal/Modal';
import SignaturePad from './SignaturePad';
import AccountHelpers from '../../../../../utils/accountHelpers';
import Api from '../../../../../api/api';
import Select from '../../../../_common/Form/Selects/Select/Select';
import ZenbexusFields from './ZenbexusFields';

const consultingSection = (productName) => {
  const fields = [
    { path: 'embryoFetalToxicity', label: 'Potential embryo-fetal toxicity' },
    { path: 'notPregnantOrBreastfeeding', label: `Not taking ${productName}® if pregnant or breastfeeding` },
    {
      path: 'twoBirthControlMethods',
      label: 'Using at the same time at least 1 highly effective method-tubal ligation, intrauterine device (IUD), hormonal (birth control pills, hormonal patches, injections, vaginal rings, or implants), or partner’s vasectomy-and at least 1 additional effective method of birth control-male latex or synthetic condom, diaphragm, or cervical cap-every time they have sex with a male, or abstaining from sex with a male',
    },
    { path: 'unacceptableBirthControlMethodsExplained', label: 'Unacceptable methods of birth control are progesterone-only “mini-pills,” IUD Progesterone T, female condoms, natural family planning (rhythm method) or breastfeeding, fertility awareness, withdrawal, and cervical shield (a cervical shield should not be confused with a cervical cap, which is an effective secondary form of contraception)' },
    { path: 'contraceptionFourWeeksBeforeDuringAfter', label: `Continuing to use at the same time at least 1 highly effective method and at least 1 additional effective method of birth control beginning at least 4 weeks before taking ${productName}, while taking ${productName}, during dose interruptions, and for at least 4 weeks after stopping ${productName} every time they have sex with a male, or abstaining from sex with a male` },
    { path: 'pregnancyTestingSchedule', label: 'Obtaining a pregnancy test-performed by their healthcare provider-weekly during the first 4 weeks of use. Thereafter, pregnancy testing should be repeated every 4 weeks during the rest of their treatment in females with regular menstrual cycles or no cycle at all. If menstrual cycles are irregular, the pregnancy testing should occur every 2 weeks' },
    { path: 'stopIfPregnantContactProvider', label: `The need to stop taking ${productName} right away in the event of becoming pregnant, or if they think for any reason they may be pregnant, and to call their healthcare provider immediately` },
    { path: 'sideEffectsDiscussed', label: 'Possible side effects include deep vein thrombosis and pulmonary embolism' },
    { path: 'mdsBloodMonitoring', label: 'The need for del 5q myelodysplastic syndromes (MDS) patients to schedule a blood test every week for the first 8 weeks and monthly thereafter to monitor blood counts while taking lenalidomide' },
    { path: 'noSharingMedication', label: `Not sharing ${productName} capsules with anyone` },
    { path: 'noBloodDonation', label: `Not donating blood while taking ${productName}` },
    { path: 'doNotBreakCapsules', label: `Not breaking, chewing, or opening ${productName} capsules` },
    { path: 'doseAdministrationInstructions', label: `Instructions on ${productName} dose and administration` },
  ];

  if (!productName.toLowerCase().startsWith('lenalidomide')) {
    return fields.filter((f) => f.path !== 'mdsBloodMonitoring');
  }

  return fields;
};

const femaleConsultingSection = (productName) => {
  let fields = [
    { path: 'femaleNonRepSideEffects', label: 'Possible side effects include neutropenia, thrombocytopenia, deep vein thrombosis, and pulmonary embolism as well as risk of myocardial infarction and stroke' },
    { path: 'femaleNonRepBloodMonitoring', label: 'The need for del 5q myelodysplastic syndromes (MDS) patients to schedule a blood test every week for the first 8 weeks and monthly thereafter to monitor blood counts while taking lenalidomide' },
    { path: 'femaleNonRepNoSharing', label: `Not sharing ${productName} capsules with anyone - especially with females who can get pregnant` },
    { path: 'femaleNonRepNoBloodDonation', label: `Not donating blood while taking ${productName} including dose interruptions) and for 4 weeks after stopping ${productName}` },
    { path: 'femaleNonRepCapsuleHandling', label: `Not breaking, chewing, or opening ${productName} capsules` },
    { path: 'femaleNonRepDoseInstructions', label: `Instructions on ${productName} dose and administration` },
  ];

  if (productName.toLowerCase().startsWith('pomalidomide')) {
    fields = fields.map((f) => (f.path === 'femaleNonRepSideEffects'
      ? {
        ...f,
        label: 'Possible side effects include deep vein thrombosis, pulmonary embolism, myocardial infarction, and stroke',
      } : f));
  }

  if (productName.toLowerCase().startsWith('lenalidomide')) {
    fields = fields.map((f) => (f.path === 'femaleNonRepSideEffects'
      ? {
        ...f,
        label: 'Possible side effects include neutropenia, thrombocytopenia, deep vein thrombosis, and pulmonary embolism as well as risk of myocardial infarction and stroke',
      } : f));
  }

  if (!productName.toLowerCase().startsWith('lenalidomide')) {
    return fields.filter((f) => f.path !== 'femaleNonRepBloodMonitoring');
  }

  return fields;
};

const maleConsultingSection = (productName) => [
  {
    path: 'maleCondomRequirement',
    label:
      'Potential embryo-fetal toxicity and contraception (wearing a latex or synthetic condom every time when engaging in sexual intercourse with a female who can get pregnant, even if the patient has had a successful vasectomy)',
  },
  {
    path: 'maleSideEffectsDiscussed',
    label: `Female partners of males taking ${productName}® must call their healthcare provider right away if they get pregnant`,
  },
  {
    path: 'maleBloodMonitoring',
    label: 'Possible side effects include deep vein thrombosis and pulmonary embolism',
  },
  {
    path: 'maleNoSharingMedication',
    label: `Not sharing ${productName} capsules with anyone - especially with females who can get pregnant`,
  },
  {
    path: 'maleNoBloodDonation',
    label: `Not donating blood or sperm while taking ${productName} (including dose interruptions) and for 4 weeks after stopping ${productName}`,
  },
  {
    path: 'maleCapsuleHandling',
    label: `Not breaking, chewing, or opening ${productName} capsules`,
  },
  {
    path: 'maleDoseInstructions',
    label: `Instructions on ${productName} dose and administration`,
  },
];

const riskCategories = [
  { path: 'zenFemaleCounseled', label: 'Females who can become pregnant' },
  { path: 'zenFemaleNonRepCounseled', label: 'Females who cannot become pregnant' },
  { path: 'zenMaleCounseled', label: 'Males' },
];

const Checklist = ({ info, changeScriptInfo }) => {
  const account = AccountHelpers.getUser();

  const isZenbexus = info.Product?.name?.toLowerCase().startsWith('zenbexus');

  const checklist = info.ProductsChecklist || {};

  const [data, setData] = useState({
    ...checklist,
    pharmacyName: 'Sortpak',
    pharmacyAddress: '655 N Central Ave 22nd Floor Glendale, Ca 91203',
    patientName: `${info.Patient.firstName} ${info.Patient.lastName}`,
    patientDob: info.Patient.dob,
    counselorName: checklist.counselorName || account.name,
    workPhone: '(877) 570-7787',
    counselingDate: checklist.counselingDate || moment().format('YYYY-MM-DD'),
    ...(isZenbexus ? {
      pharmacyAddress: '655 N Central Ave 22nd Floor',
      pharmacyCity: 'Glendale',
      pharmacyState: 'CA',
      pharmacyZip: '91203',
      pharmacyNPI: '1063407252',
      patientFirstName: info.Patient.firstName,
      patientLastName: info.Patient.lastName,
      physicianFirstName: info.Physician?.firstName || '',
      physicianLastName: info.Physician?.lastName || '',
      physicianNPI: info.Physician?.NPI || '',
    } : {}),
  });

  const [errors, setErrors] = useState({});

  const [isTouched, touchToggle] = useState(false);

  const [isLogsModalOpen, logsModalOpenToggle] = useState(false);
  const [isSignModalOpen, signModalOpenToggle] = useState(false);

  const [saveLoading, saveLoadingToggle] = useState(false);
  const [signLoading, signLoadingToggle] = useState(false);

  const changeData = (path, value) => {
    setData((prev) => ({ ...prev, [path]: value }));

    setErrors((prev) => ({ ...prev, [path]: '' }));

    touchToggle(true);
  };

  const closeModal = () => {
    signModalOpenToggle(false);
  };

  const updateScriptChecklist = async (signed) => {
    const sendingData = { ...data };

    if (isZenbexus) {
      sendingData.riskCategory = riskCategories.find((c) => sendingData[c.path])?.label || '';
    }

    const { data: { checklistLog } } = await Api.updateScriptChecklist(sendingData, signed);

    touchToggle(false);

    if (checklistLog) {
      changeScriptInfo({
        ...info,
        ProductsChecklist: {
          ...sendingData,
          checklistUpdateLogs: [...(info.ProductsChecklist.checklistUpdateLogs || []), checklistLog],
        },
      });
    }
  };

  const sign = async (attachmentFile) => {
    signLoadingToggle(true);
    const { id, ScriptId, ProductId } = info.ProductsChecklist;

    await updateScriptChecklist(true);

    const { data: { checklistLog, url } } = await Api.signScriptChecklist({
      id, ScriptId, ProductId, attachmentFile,
    });

    changeScriptInfo({
      ...info,
      ProductsChecklist: {
        ...data,
        counselorSignature: url,
        checklistUpdateLogs: [...(info.ProductsChecklist.checklistUpdateLogs || []), checklistLog],
      },
    });
    setData((prev) => ({ ...prev, counselorSignature: url }));

    signLoadingToggle(false);
  };

  const save = async () => {
    // const requiredFields = ['authorizationNo', 'confirmationNo', 'counselingDate'];
    // let hasErrors = false;
    //
    // requiredFields.forEach((field) => {
    //   if (!data[field]?.trim()) {
    //     hasErrors = true;
    //     setErrors((prev) => ({ ...prev, [field]: 'Field is required' }));
    //   }
    // });
    //
    // if (hasErrors) {
    //   toast.error('Please fill this fields');
    // } else {
    saveLoadingToggle(true);

    try {
      await updateScriptChecklist();

      toast.success('Successfully saved');
    } catch (e) {
      toast.error(e.response?.data?.error?.message || 'Unable to save the checklist. Please try again.');
    }

    saveLoadingToggle(false);
    // }
  };

  const downloadScriptChecklist = async (checklistId) => {
    signLoadingToggle(true);

    await updateScriptChecklist(!isTouched ? true : undefined);
    await Api.downloadScriptChecklist(checklistId);

    signLoadingToggle(false);
  };

  const onKeyPress = (e, validationType, isPaste) => {
    if (validationType === 'number') {
      utils.keyPressOnlyNumber(e, isPaste);
    } else if (validationType === 'phone') {
      utils.keyPressPhoneNumber(e, isPaste);
    }
  };

  const disableFields = data.counselorSignature;

  const showDownloadButton = (data.counselorSignature && data.authorizationNo && data.confirmationNo)
  || (isZenbexus && data.confirmationNo && data.confirmationDate);

  return (
    <div className={classes.checklist_wrapper}>
      <div className={classes.header_title}>
        Education and Counseling Checklist for Pharmacies

        {info.ProductsChecklist.checklistUpdateLogs?.length && (
          <div
            onClick={() => logsModalOpenToggle(true)}
          >
            i
          </div>
        )}
      </div>

      {isZenbexus
        ? (
          <ZenbexusFields
            data={data}
            errors={errors}
            changeData={changeData}
            riskCategories={riskCategories}
            disableFields={disableFields}
          />
        )

        : (
          <>
            <div className={classes.header_subtitle}>
              {`${info.Product.name} Risk Evaluation and Mitigation Strategy (REMS) program education and prescribing safety`}
            </div>

            <div className={classes.header_box}>
              <div className={classes.inputs_row}>
                <InputField
                  label="Authorization No."
                  height={35}
                  labelDefaultPosition={5}
                  value={data.authorizationNo}
                  onChange={(v) => changeData('authorizationNo', v)}
                  error={errors.authorizationNo}
                />

                <InputField
                  label="Confirmation No."
                  height={35}
                  labelDefaultPosition={5}
                  value={data.confirmationNo}
                  onChange={(v) => changeData('confirmationNo', v)}
                  error={errors.confirmationNo}
                />

                <DOBPicker
                  label="Confirmation Date"
                  hideClearButton
                  height={35}
                  labelDefaultPosition={5}
                  date={data.confirmationDate}
                  wrapperClassName={classes.calendar_wrapper}
                  dateFormat="YYYY-MM-DD"
                  onChange={(d) => changeData('confirmationDate', d)}
                  disabled={disableFields}
                />
              </div>

              <InputField
                label="Pharmacy Name"
                height={35}
                labelDefaultPosition={5}
                value={data.pharmacyName}
                disabled
              />

              <InputField
                label="Pharmacy Address"
                height={35}
                labelDefaultPosition={5}
                value={data.pharmacyAddress}
                disabled
              />

              <div className={classes.inputs_row}>
                <InputField
                  label="Counselor Name"
                  height={35}
                  labelDefaultPosition={5}
                  value={data.counselorName}
                  disabled
                />

                <InputField
                  label="Work Phone"
                  height={35}
                  labelDefaultPosition={5}
                  value={data.workPhone}
                  disabled
                />

                <InputField
                  label="Ext."
                  height={35}
                  labelDefaultPosition={5}
                  value={data.ext}
                  onChange={(v) => changeData('ext', v)}
                  disabled={disableFields}
                />
              </div>

              <div className={classes.inputs_row}>
                <InputField
                  label="Patient Name"
                  height={35}
                  labelDefaultPosition={5}
                  value={data.patientName}
                  disabled
                />

                <DOBPicker
                  label="Date of Birth"
                  hideClearButton
                  height={35}
                  labelDefaultPosition={5}
                  date={data.patientDob}
                  wrapperClassName={classes.calendar_wrapper}
                  dateFormat="YYYY-MM-DD"
                  disabled
                />
              </div>

              <Select
                label="Risk Category"
                height={35}
                labelDefaultPosition={5}
                arrowTop={12}
                currentOption={data.riskCategory}
                options={['ADULT FEMALE - NOT OF CHILDBEARING POTENTIAL', 'ADULT MALE']}
                onChange={(option) => changeData('riskCategory', option)}
                zIndex={30}
                disabled={disableFields}
              />
            </div>

            <div className={classes.section_title}>
              Checklist for female patients of reproductive potential
            </div>

            <Checkbox
              label="I will make sure that patients are aware that they will receive the Medication Guide along with their prescription"
              checked={data.femaleMedicationGuideProvided}
              onChange={(check) => changeData('femaleMedicationGuideProvided', check)}
              readOnly={disableFields}
            />

            <div className={classes.sub_title}>
              I COUNSELED ADULTS AND CHILDREN ON:
            </div>

            {consultingSection(info.Product.name).map(({ path, label }) => (
              <Checkbox
                key={path}
                label={label}
                checked={data[path]}
                onChange={(check) => changeData(path, check)}
                readOnly={disableFields}
              />
            ))}

            <div className={classes.inputs_row}>
              <InputField
                label="Milligram (mg) Strength"
                height={35}
                labelDefaultPosition={5}
                value={data.femaleNonRepMilligramStrength}
                onChange={(v) => changeData('femaleNonRepMilligramStrength', v)}
                onKeyPress={(e) => onKeyPress(e, 'number')}
                onPaste={(e) => onKeyPress(e, 'number', true)}
                disabled={disableFields}
              />

              <InputField
                label="Number of Capsules Dispensed"
                height={35}
                labelDefaultPosition={5}
                value={data.femaleNonRepCapsulesDispensed}
                onChange={(v) => changeData('femaleNonRepCapsulesDispensed', v)}
                onKeyPress={(e) => onKeyPress(e, 'number')}
                onPaste={(e) => onKeyPress(e, 'number', true)}
                disabled={disableFields}
              />
            </div>

            <div className={classes.sub_title}>
              FEMALE CHILDREN (&lt;18 YEARS OF AGE):
            </div>

            <Checkbox
              label={`Parent or legal guardian must have read the ${info.Product.name} REMS education material and agreed to ensure compliance`}
              checked={data.femaleChildParentGuideRead}
              onChange={(check) => changeData('femaleChildParentGuideRead', check)}
              readOnly={disableFields}
            />

            <div className={classes.section_title}>
              Checklist for female patients not of reproductive potential
            </div>

            <Checkbox
              label="I will make sure that patients are aware that they will receive the Medication Guide along with their prescription"
              checked={data.femaleNonRepMedicationGuideProvided}
              onChange={(check) => changeData('femaleNonRepMedicationGuideProvided', check)}
              readOnly={disableFields}
            />

            <div className={classes.sub_title}>
              I COUNSELED ADULTS AND CHILDREN ON:
            </div>

            {femaleConsultingSection(info.Product.name).map(({ path, label }) => (
              <Checkbox
                key={path}
                label={label}
                checked={data[path]}
                onChange={(check) => changeData(path, check)}
                readOnly={disableFields}
              />
            ))}

            <div className={classes.inputs_row}>
              <InputField
                label="Milligram (mg) Strength"
                height={35}
                labelDefaultPosition={5}
                value={data.femaleMilligramStrength}
                onChange={(v) => changeData('femaleMilligramStrength', v)}
                onKeyPress={(e) => onKeyPress(e, 'number')}
                onPaste={(e) => onKeyPress(e, 'number', true)}
                disabled={disableFields}
              />

              <InputField
                label="Number of Capsules Dispensed"
                height={35}
                labelDefaultPosition={5}
                value={data.femaleCapsulesDispensed}
                onChange={(v) => changeData('femaleCapsulesDispensed', v)}
                onKeyPress={(e) => onKeyPress(e, 'number')}
                onPaste={(e) => onKeyPress(e, 'number', true)}
                disabled={disableFields}
              />
            </div>

            <div className={classes.sub_title}>
              FEMALE CHILDREN (&lt;18 YEARS OF AGE):
            </div>

            <Checkbox
              label={`Parent or legal guardian must have read the ${info.Product.name} REMS education material and agreed to ensure compliance`}
              checked={data.femaleNonRepChildParentGuideRead}
              onChange={(check) => changeData('femaleNonRepChildParentGuideRead', check)}
              readOnly={disableFields}
            />

            <Checkbox
              label="Parent or legal guardian must inform the child’s healthcare provider when the child begins menses"
              checked={data.femaleNonRepChildParentInformProvider}
              onChange={(check) => changeData('femaleNonRepChildParentInformProvider', check)}
              readOnly={disableFields}
            />

            <div className={classes.section_title}>
              Checklist for male patients
            </div>

            <Checkbox
              label="I will make sure that patients are aware that they will receive the Medication Guide along with their prescription"
              checked={data.maleMedicationGuideProvided}
              onChange={(check) => changeData('maleMedicationGuideProvided', check)}
              readOnly={disableFields}
            />

            <div className={classes.sub_title}>
              I COUNSELED ADULTS AND CHILDREN ON:
            </div>

            {maleConsultingSection(info.Product.name).map(({ path, label }) => (
              <Checkbox
                key={path}
                label={label}
                checked={data[path]}
                onChange={(check) => changeData(path, check)}
                readOnly={disableFields}
              />
            ))}

            <div className={classes.inputs_row}>
              <InputField
                label="Milligram (mg) Strength"
                height={35}
                labelDefaultPosition={5}
                value={data.maleMilligramStrength}
                onChange={(v) => changeData('maleMilligramStrength', v)}
                onKeyPress={(e) => onKeyPress(e, 'number')}
                onPaste={(e) => onKeyPress(e, 'number', true)}
                disabled={disableFields}
              />

              <InputField
                label="Number of Capsules Dispensed"
                height={35}
                labelDefaultPosition={5}
                value={data.maleCapsulesDispensed}
                onChange={(v) => changeData('maleCapsulesDispensed', v)}
                onKeyPress={(e) => onKeyPress(e, 'number')}
                onPaste={(e) => onKeyPress(e, 'number', true)}
                disabled={disableFields}
              />
            </div>

            <div className={classes.sub_title}>
              MALE CHILDREN (&lt;18 YEARS OF AGE):
            </div>

            <Checkbox
              label={`Parent or legal guardian must have read the ${info.Product.name} REMS® education material and agreed to ensure compliance`}
              checked={data.maleChildParentGuideRead}
              onChange={(check) => changeData('maleChildParentGuideRead', check)}
              readOnly={disableFields}
            />
          </>
        )}

      {!isZenbexus && (
        <div className={classes.footer_note}>
          All boxes and spaces must be marked or filled in during counseling with the patient for every prescription.
        </div>
      )}

      <div className={classes.inputs_row}>
        <DOBPicker
          label="Date"
          hideClearButton
          height={35}
          labelDefaultPosition={5}
          date={data.counselingDate}
          wrapperClassName={classes.calendar_wrapper}
          dateFormat="YYYY-MM-DD"
          error={errors.counselingDate}
          onChange={(d) => changeData('counselingDate', d)}
          disabled={disableFields}
        />

        <div className={classes.download_btn_wrapper}>
          {!isZenbexus && (
            <p>
              Counselor Signature

              {data.counselorSignature
                ? <img src={data.counselorSignature} alt="counselor signature" />

                : (
                  <Button
                    title="Sign and Save"
                    className={classes.sign_btn}
                    editButton
                    reverseColor
                    onClick={() => signModalOpenToggle(true)}
                  />
                )}
            </p>
          )}

          <div className={classes.save_btn_wrapper}>
            {showDownloadButton
              && (
                <Button
                  title="Download"
                  className={classes.download_btn}
                  downloadButton
                  reverseColor
                  loading={signLoading}
                  onClick={() => downloadScriptChecklist(data.id)}
                />
              )}

            <Button
              title="Save"
              className={classes.save_btn}
              loading={saveLoading}
              onClick={save}
            />
          </div>
        </div>
      </div>

      <Modal
        open={isSignModalOpen}
        onClose={() => !signLoading && closeModal()}
      >
        <SignaturePad
          onClose={() => !signLoading && closeModal()}
          onSave={sign}
          saveLoading={signLoading}
        />
      </Modal>

      <Modal
        open={isLogsModalOpen}
        onClose={() => logsModalOpenToggle(false)}
        className={classes.log_modal}
      >
        <h2 style={{ color: '#5F6C8E' }}>Logs</h2>

        <div className={classes.logs_wrapper}>
          {info.ProductsChecklist.checklistUpdateLogs?.map((l) => (
            <div key={l.id} className={classes.log_wrapper}>
              <div>
                <p>Operation:</p>
                <p>{l.operation}</p>
              </div>

              <div>
                <p>User:</p>
                <p>{l.userName}</p>
              </div>

              <div>
                <p>Date:</p>
                <p>{moment(l.occurredOn).format('MM.DD.YYYY hh:mm A')}</p>
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
};

Checklist.propTypes = {
  info: PropTypes.object.isRequired,
  changeScriptInfo: PropTypes.func.isRequired,
};

export default Checklist;
